import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/calendar/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/calendar/ui/form'
import { Input } from '@/components/calendar/ui/input'
import { Button } from '@/components/calendar/ui/button'
import { useCalendarContext } from '../calendar-context'
import { DateTimePicker } from '@/components/calendar/ui/date-time-picker'
import { Combobox } from '@/components/calendar/ui/combobox'
import { ColorPickerLabeled } from '@/components/calendar/ui/color-picker-labeled'
import { addReservation } from '@/rental/services/reservation-service'
import { CalendarEvent, CalendarVehicle } from '../calendar-types'
import { getCurrencySymbol } from '@/lib/utils'
import { useEffect, useState } from 'react'
import { getAvailableVehicles } from '@/rental/services/reservation-service'
import { toast } from 'sonner'
import { useUserContext } from '@/context/UserContext'

const formSchema = z
  .object({
    pickupId: z.number(),
    dropoffId: z.number(),
    vehicleId: z.number(),
    previewPrice: z.string().optional(),
    price: z.string().optional(),
    discount: z.string().optional(),
    start: z.string().datetime(),
    end: z.string().datetime(),
    customerName: z.string().optional(),
    customerEmail: z.string().optional(),
    customerPhone: z.string().optional(),
    partnerId: z.number().optional(),
    notes: z.string().optional(),
    color: z.string(),
    status: z.string().optional(),
  })
  .refine(
    (data) => {
      const start = new Date(data.start)
      const end = new Date(data.end)
      return end >= start
    },
    {
      message: 'End time must be after start time',
      path: ['end'],
    }
  );

export default function CalendarNewEventDialog() {
  const {
    newEventDialogOpen,
    setNewEventDialogOpen,
    events,
    setEvents,
    vehicles,
    locations,
    partners,
    reservationStates,
  } = useCalendarContext();

  const [availableVehicles, setAvailableVehicles] = useState<CalendarVehicle[]>([]);
  const [isUserPartner, setIsUserPartner] = useState<boolean>(false);
  const [userId, setUserId] = useState<number | undefined>(undefined);
  const [currency, setCurrency] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { decodedToken } = useUserContext();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      pickupId: undefined,
      dropoffId: undefined,
      vehicleId: undefined,
      previewPrice: '',
      price: '',
      discount: '',
      customerEmail: '',
      customerName: '',
      customerPhone: '',
      partnerId: undefined,
      notes: '',
      start: '',
      end: '',
      color: 'blue',
      status: 'approved',
    },
  });

  async function loadVehicles() {
    const pickup = form.getValues().start;
    const dropOff = form.getValues().end;

    if (pickup && dropOff) {
      const pickupDate = new Date(pickup);
      const dropOffDate = new Date(dropOff);

      if (dropOffDate > pickupDate) {
        const vehicles = await getAvailableVehicles(new Date(pickup), new Date(dropOff));
        setAvailableVehicles(vehicles || []);
      }
    }
  }

  form.watch((value, field) => {
    if (field.name === 'vehicleId') {
      const vehicleId = form.getValues().vehicleId;
      if (vehicleId) {
        const vehicle = availableVehicles.find(v => v.id === vehicleId);
        if (vehicle) {
          const price = vehicle.totalPrice;
          form.setValue('previewPrice', price ? price.toString() : '');
        }
      }
    }

    if (field.name === 'previewPrice' || field.name === 'discount') {
      const discount = parseFloat(value.discount || '0');
      const previewPrice = parseFloat(value.previewPrice || '');
      if (!isNaN(previewPrice)) {
        const finalPrice = discount > 0 ? previewPrice - (previewPrice * (discount / 100)) : previewPrice;
        form.setValue('price', finalPrice.toString());
      } else {
        form.setValue('price', '');
      }
    }
  });

  useEffect(() => {
    form.reset();
    setAvailableVehicles([]);

    const currency = getCurrencySymbol(decodedToken?.permissions?.currency || 'EUR');
    setCurrency(currency);
    setIsUserPartner(decodedToken?.permissions?.user_role === 'commission_partner');
    //setIsUserPartner(true);
    setUserId(decodedToken?.user?.id || undefined);

  }, [newEventDialogOpen]);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      setIsLoading(true);
      let newEvent = {
        id: crypto.randomUUID(),
        title: vehicles.find(v => v.id === values.vehicleId)?.name || 'New Reservation',
        ...values,
        start: new Date(values.start),
        end: new Date(values.end),
        color: reservationStates.find(x => x.value === values.status)?.color || 'blue',
        price: values.price ? parseFloat(values.price) : undefined,
        discount: values.discount ? parseFloat(values.discount) : undefined,
        discountType: 'percentage',
      } as CalendarEvent;

      if (isUserPartner) {
        newEvent.partnerId = userId;
        newEvent.status = 'pending';
      }

      // Save the new reservation 
      const createdEvent = await addReservation(newEvent);

      if(createdEvent) {
        newEvent = createdEvent;
      }

      setEvents([...events, newEvent]);
      setNewEventDialogOpen(false);
      form.reset();
    } catch (error: any) {
      console.error('Error while submitting form:', error);
      toast.error(error.response?.data?.message || 'Failed to add reservation. Please try again.');
    }
    finally {
      setIsLoading(false);
    }
  }

  return (
    <Dialog open={newEventDialogOpen} onOpenChange={setNewEventDialogOpen} modal={false} >
      <DialogContent className='max-w-2xl w-full'>
        <DialogHeader>
          <DialogTitle>New Reservation</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="gap-3 grid grid-cols-2 space-y-2">

            <FormField
              control={form.control}
              name="pickupId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Pickup</FormLabel>
                  <FormControl>
                    <Combobox field={field} options={locations} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dropoffId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Dropoff</FormLabel>
                  <FormControl>
                    <Combobox field={field} options={locations} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="start"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Start</FormLabel>
                  <FormControl>
                    <DateTimePicker field={field} onChange={loadVehicles} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="end"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">End</FormLabel>
                  <FormControl>
                    <DateTimePicker field={field} onChange={loadVehicles} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="vehicleId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Vehicle</FormLabel>
                  <FormControl>
                    <Combobox field={field} options={availableVehicles} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!isUserPartner &&
              <FormField
                control={form.control}
                name="partnerId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-bold">Commision partner</FormLabel>
                    <FormControl>
                      <Combobox field={field} options={partners} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            }
            {isUserPartner &&
              <div></div>
            }

            <div className='grid grid-cols-2 gap-3'>
              <FormField
                control={form.control}
                name="previewPrice"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-bold">Base price ({currency})</FormLabel>
                    <FormControl>
                      <Input type='number' placeholder="Base price" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="discount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-bold">Discount(%)</FormLabel>
                    <FormControl>
                      <Input type='number' min={0} max={100} placeholder="Discount %" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

            </div>

            <FormField
              control={form.control}
              name="price"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold" style={{ color: "green" }}>Final price ({currency})</FormLabel>
                  <FormControl>
                    <Input type='number' readOnly placeholder="Final price" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="customerName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Customer name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="customerEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Email</FormLabel>
                  <FormControl>
                    <Input type='email' placeholder="Customer email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="customerPhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Phone number</FormLabel>
                  <FormControl>
                    <Input placeholder="Customer phone number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Notes</FormLabel>
                  <FormControl>
                    <Input placeholder="Notes" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!isUserPartner &&
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-bold">Status</FormLabel>
                    <FormControl>
                      <ColorPickerLabeled field={field} options={reservationStates} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            }

            <div className="flex justify-end col-span-2">
              <Button type="submit" disabled={isLoading}>Add Reservation </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
